import { APP_INITIALIZER, ApplicationConfig, inject } from "@angular/core";
import { provideRouter, Router } from "@angular/router";

import { HttpClient, provideHttpClient, withInterceptors } from "@angular/common/http";
import { provideAnimations } from "@angular/platform-browser/animations";
import { AuthConfig, AuthService } from "@mes-slalom/ui-common-lib";
import { environment } from "../environments/environment";
import { routes } from "./app-routing.module";
import { EnvironmentGuard } from "./guards/environment.guard";
import { PendingChangesGuard } from "./guards/pending-changes.guard";
import {
  getRetryUrl,
  isUnrecoverableAuthError,
  runWithRetry,
  showAuthInitError,
} from "./helpers/auth-init.helper";
import { authInterceptor } from "./interceptors/auth.interceptor";
import { loadingInterceptor } from "./interceptors/loading.interceptor";
import { CoreStore } from "./store/core.store";

const TOKENS_STORAGE_KEY = "tokens";
// Set when we restart login after a rejected code, so a second rejection shows an
// error instead of bouncing the user between the app and the login page forever.
const LOGIN_RESTARTED_KEY = "authInitLoginRestarted";

function clearLoginParamsFromUrl(): void {
  window.history.replaceState(
    window.history.state,
    "",
    getRetryUrl(window.location.pathname, window.location.search)
  );
}

function failAuthInit(err: unknown): never {
  console.error("Sign-in failed during app start", err);
  showAuthInitError(document, () => {
    sessionStorage.removeItem(LOGIN_RESTARTED_KEY);
    window.location.assign(getRetryUrl(window.location.pathname, window.location.search));
  });
  // Rethrow so Angular doesn't render the app without a signed-in user.
  throw err;
}

export function initAuth() {
  const auth = inject(AuthService);
  const core = inject(CoreStore);
  return async () => {
    try {
      await runWithRetry(() => auth.handleAuthCallback());
    } catch (err) {
      if (isUnrecoverableAuthError(err) && !sessionStorage.getItem(LOGIN_RESTARTED_KEY)) {
        // The login code was rejected (e.g. already used). Start a fresh login.
        sessionStorage.setItem(LOGIN_RESTARTED_KEY, "true");
        localStorage.removeItem(TOKENS_STORAGE_KEY);
        clearLoginParamsFromUrl();
        auth.loginStart();
        return;
      }
      failAuthInit(err);
    }

    if (!localStorage.getItem(TOKENS_STORAGE_KEY)) {
      auth.loginStart();
      return;
    }

    try {
      const employee = auth.getEmployee();
      core.setUser(employee);
      const roles = (employee.securityRoles || []).map((s: any) => s.name);
      core.setSecurityRoles(roles);
      sessionStorage.removeItem(LOGIN_RESTARTED_KEY);
    } catch (err) {
      // The stored token couldn't be read. Drop it so "Try again" starts a clean login.
      localStorage.removeItem(TOKENS_STORAGE_KEY);
      failAuthInit(err);
    }
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    provideAnimations(),
    provideHttpClient(withInterceptors([authInterceptor, loadingInterceptor])),
    EnvironmentGuard,
    PendingChangesGuard,
    {
      provide: AuthService,
      useFactory: () => {
        const http = inject(HttpClient);
        const router = inject(Router);
        const config: AuthConfig = {
          authEndpoint: environment.auth_endpoint,
          clientId: environment.clientId,
          redirectUri: environment.redirectUri,
        };
        return new AuthService(http, router, config);
      },
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initAuth,
      multi: true,
    },
  ],
};
